import { MessageDescriptor, useIntl } from 'react-intl';

import Image from '@/components/image/NextImage';
import { useAnalytics } from '@/hooks/analytics';
import { useRouterPush } from '@/hooks/utils/useRouterPush';

import AudioPlayer from '../AudioPlayer';
import { MentalHealthSectionTitle, MentalHealthSlide } from '../MentalHealthTools/types';
import messages from './messages';
import {
	AppCopy,
	AppImage,
	AppSubheading,
	AppTitle,
	ContentAuthor,
	ContentLink,
	ContentTitle,
	Copy,
	CopyContainer,
	Heading,
	SampleCta,
	SlideCTA,
	Wrapper,
} from './styles';

const ToolsSlide = (
	props: {
		slide: MentalHealthSlide;
		sectionTitle: string;
		currentSection: MentalHealthSectionTitle;
		analyticsPrefix?: string;
		hideFreeSampleLabel?: boolean;
		ctaConfig?: {
			label: MessageDescriptor;
			onClick: () => void;
		};
		borderBakedIn?: boolean;
	} & React.HTMLAttributes<HTMLDivElement>,
) => {
	const {
		slide,
		sectionTitle,
		currentSection,
		analyticsPrefix = 'Homepage',
		hideFreeSampleLabel = false,
		ctaConfig,
		borderBakedIn,
	} = props;
	const { formatMessage } = useIntl();
	const routerPush = useRouterPush();
	const { logEvent } = useAnalytics();

	const onContentClick = async (slide: MentalHealthSlide) => {
		if (!slide.contentUrl) return;

		logEvent({
			eventName: `${analyticsPrefix} : CTA : Clicked`,
			eventProps: {
				content_title: slide?.contentTitle?.defaultMessage as string,
			},
		});
		await routerPush(slide.contentUrl);
	};

	return (
		<Wrapper {...props} $isVisible={sectionTitle === currentSection}>
			<CopyContainer>
				<Heading>{formatMessage(slide.heading)}</Heading>
				<Copy>{formatMessage(slide.copy)}</Copy>
				{slide.audioSrc && (
					<>
						{!hideFreeSampleLabel && <SampleCta>{formatMessage(messages.sampleCta)}</SampleCta>}
						<ContentLink
							as={slide.contentUrl ? 'a' : 'span'}
							onClick={() => onContentClick(slide)}
							href={slide.contentUrl}
						>
							{slide.contentTitle && <ContentTitle>{formatMessage(slide.contentTitle)}</ContentTitle>}
						</ContentLink>
						{slide.author && <ContentAuthor>{formatMessage(slide.author)}</ContentAuthor>}
						<AudioPlayer analyticsPrefix={analyticsPrefix} slide={slide} currentSection={currentSection} />
						{ctaConfig && (
							<SlideCTA backgroundColor="accessibleBrandGradient" onPress={ctaConfig.onClick}>
								{formatMessage(ctaConfig.label, {
									contentType: formatMessage(slide.heading),
								})}
							</SlideCTA>
						)}
					</>
				)}
			</CopyContainer>
			<AppImage borderBakedIn={borderBakedIn}>
				<Image
					loading="lazy"
					style={{ objectFit: 'cover' }}
					fill
					src={slide.imageUrl}
					alt={`App image for ${formatMessage(slide.title)}`}
				/>
				<AppCopy>
					{slide.contentTitle && <AppTitle>{formatMessage(slide.contentTitle)}</AppTitle>}
					{slide.contentSubheading && <AppSubheading>{formatMessage(slide.contentSubheading)}</AppSubheading>}
				</AppCopy>
			</AppImage>
		</Wrapper>
	);
};

export default ToolsSlide;
